<template>
  <div
    ref="tabsRef"
    class="flex shrink-0 items-center gap-1.5 font-ds"
  >
    <draggable
      :model-value="configsList"
      v-bind="DRAG_OPTIONS"
      :disabled="configsList.length === 1 || readonly"
      handle="[data-handle]"
      class="flex gap-1 bg-accent dark:bg-background p-1 rounded-ds-md overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20"
      @remove="handleRemoveElement"
      @sort="handleSort"
    >
      <template #item="{ element, index }">
        <PivotTab
          :readonly
          :label="element?.name"
          :active="index === configIndex"
          :is-last-one="configsList.length === 1"
          :edit-on-render="index === addedIndex"
          @duplicate="handleDuplicate(index, element)"
          @remove="handleRemove(index)"
          @update="handleUpdate(index, $event)"
          @click="handleClick(index)"
          @pin="handlePinClick(index)"
        />
      </template>
    </draggable>
    <DSButton
      v-if="!readonly"
      variant="ghost"
      size="icon-sm"
      class="size-7 shrink-0 text-muted-foreground"
      aria-label="Add new configuration"
      @click="handleCreate"
    >
      <Plus class="size-4" />
    </DSButton>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'
import { Plus } from '@lucide/vue'

import { PivotTableConfig, PivotTableStorage } from '@types'

import { DSButton } from '@ds'

import PivotTab from './primitives/PivotTab.vue'

// Mirrors vuedraggable's own defaults for this kind of single-row tab strip.
const DRAG_OPTIONS = {
  animation: 200,
  itemKey: (element: string) => element,
  group: {
    name: 'pivot-configs-tabs',
    put: false,
    pull: true,
  },
}

type Props = {
  configsList: PivotTableConfig[]
  configIndex: number
  defaultConfig: PivotTableConfig
  readonly?: boolean
}

type Emits = {
  'update:storage': [data: PivotTableStorage]
  'pin': [data: PivotTableConfig, callback: () => void, index?: number]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const addedIndex = ref<number>()

const tabsRef = useTemplateRef('tabsRef')

const handleSort = (event: any) => {
  const configs = cloneDeep(props.configsList)
  if (!configs) return
  const element = configs[event.oldIndex]
  configs.splice(event.oldIndex, 1)
  configs.splice(event.newIndex, 0, element)
  let index = props.configIndex
  if (index === event.oldIndex) {
    index = event.newIndex
  } else if (event.oldIndex < index && event.newIndex >= index) {
    index--
  } else if (event.oldIndex > index && event.newIndex <= index) {
    index++
  }
  emit('update:storage', { configs, index })
}

const handleCreate = () => {
  const newConfig = cloneDeep(props.defaultConfig)
  const index = props.configsList.length
  addedIndex.value = index
  emit('update:storage', {
    configs: [...props.configsList, newConfig],
    index,
  })
}

const handleDuplicate = (itemIndex: number, data: PivotTableConfig) => {
  const newConfig = cloneDeep(data)
  const configs = cloneDeep(props.configsList)
  configs.splice(itemIndex, 0, newConfig)
  const index = itemIndex + 1
  addedIndex.value = index
  emit('update:storage', { configs, index })
}

const handleRemove = (itemIndex: number) => {
  const configs = cloneDeep(props.configsList)
  configs.splice(itemIndex, 1)
  let index = props.configIndex
  if (itemIndex === props.configIndex) {
    index = 0
  } else if (itemIndex < props.configIndex) {
    index--
  }
  emit('update:storage', { configs, index })
}

const handlePinClick = (itemIndex: number, newIndex?: number) => {
  const configs = cloneDeep(props.configsList[itemIndex])
  emit('pin', configs, () => {
    handleRemove(itemIndex)
  }, newIndex)
}

const handleUpdate = (itemIndex: number, name: string) => {
  const configs = props.configsList
  configs[itemIndex].name = name
  emit('update:storage', {
    configs,
    index: props.configIndex,
  })
}

const handleClick = (index: number) => {
  emit('update:storage', {
    configs: props.configsList,
    index,
  })
}

const scrollToActiveTab = async (delay = 500) => {
  await nextTick()
  setTimeout(() => {
    const container = tabsRef.value
    const activeTab = container?.querySelector('[data-active="true"]') as HTMLElement
    if (!container || !activeTab) return

    const containerRect = container.getBoundingClientRect()
    const activeTabRect = activeTab.getBoundingClientRect()

    const isFullyVisible =
      activeTabRect.left >= containerRect.left &&
      activeTabRect.right <= containerRect.right

    if (!isFullyVisible) {
      const containerWidth = containerRect.width
      const activeTabWidth = activeTabRect.width
      const activeTabLeft = activeTab.offsetLeft

      const targetScrollLeft = activeTabLeft - (containerWidth / 2) + (activeTabWidth / 2)

      const maxScrollLeft = container.scrollWidth - containerWidth
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft))

      container.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth',
      })
    }
  }, delay)
}

const handleRemoveElement = (event: any) => {
  handlePinClick(event.oldIndex, event.newIndex)
}

onMounted(scrollToActiveTab)

watch(addedIndex, async value => {
  if (value === undefined) return
  await nextTick()
  addedIndex.value = undefined
})

watch(
  () => props.configIndex,
  () => {
    scrollToActiveTab(0)
  },
)
</script>
